import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../store";
import {
  handleMessageTimeout,
  selectConversation,
  updateAiMessageFromSubscription,
} from "../store/aiChatSlice";

// Timeout thresholds
const STALE_MESSAGE_THRESHOLD_MS = 90000; // 90 seconds - trigger refetch
const MESSAGE_TIMEOUT_MS = 120000; // 120 seconds - show error

// Backstop thresholds for a persisted "pending" placeholder (covers the case
// where isSending was cleared once the placeholder arrived, so the timers above
// no longer apply). These guard against a silently-missed onUpdate.
const PENDING_REFETCH_MS = 60000; // 60s - refetch to recover a missed update
const PENDING_TIMEOUT_MS = 300000; // 5 min - give up and mark the row errored in the UI

/**
 * Hook to handle message timeouts.
 * - After 90 seconds of waiting for a response, refetch the conversation
 *   to check if the message actually arrived (handles silent subscription failures)
 * - After 120 seconds, show a timeout error to the user
 */
export function useMessageTimeout() {
  const dispatch = useDispatch<AppDispatch>();
  const { isSending, pendingSendTimestamp, currentConversation, messages } =
    useSelector((state: RootState) => state.aiChat);

  const staleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRefetchedRef = useRef(false);

  // Backstop for a persisted "pending" placeholder in the active conversation:
  // refetch once to recover a missed onUpdate, then mark it errored if it never
  // resolves. The Lambda's router timeout is the primary guarantee; this is the
  // last-resort net so the UI never spins forever.
  const conversationId = currentConversation?.id;
  const pendingMessage = messages.find(
    (m) => !m.isUser && m.status === "pending" && !!m.createdAt
  );
  const pendingMessageId = pendingMessage?.id;
  const pendingMessageCreatedAt = pendingMessage?.createdAt;
  const pendingRefetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!conversationId || !pendingMessageId || !pendingMessageCreatedAt) {
      return;
    }

    const startedAt = new Date(pendingMessageCreatedAt).getTime();
    if (Number.isNaN(startedAt)) {
      return;
    }
    const elapsed = Date.now() - startedAt;
    const refetchRemaining = PENDING_REFETCH_MS - elapsed;
    const errorRemaining = PENDING_TIMEOUT_MS - elapsed;

    const markErrored = () => {
      dispatch(
        updateAiMessageFromSubscription({
          conversationId,
          messageId: pendingMessageId,
          updates: {
            status: "error",
            text: "This response timed out. Please try again.",
          },
        })
      );
    };

    let refetchTimer: ReturnType<typeof setTimeout> | null = null;
    let errorTimer: ReturnType<typeof setTimeout> | null = null;

    // Refetch once per pending message to catch a silently-missed update.
    if (pendingRefetchedRef.current !== pendingMessageId) {
      const doRefetch = () => {
        pendingRefetchedRef.current = pendingMessageId;
        dispatch(selectConversation(conversationId));
      };
      if (refetchRemaining <= 0) {
        doRefetch();
      } else {
        refetchTimer = setTimeout(doRefetch, refetchRemaining);
      }
    }

    if (errorRemaining <= 0) {
      markErrored();
    } else {
      errorTimer = setTimeout(markErrored, errorRemaining);
    }

    return () => {
      if (refetchTimer) clearTimeout(refetchTimer);
      if (errorTimer) clearTimeout(errorTimer);
    };
  }, [conversationId, pendingMessageId, pendingMessageCreatedAt, dispatch]);

  useEffect(() => {
    // Clear any existing timeouts
    if (staleTimeoutRef.current) {
      clearTimeout(staleTimeoutRef.current);
      staleTimeoutRef.current = null;
    }
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    // Reset refetch flag when we're no longer sending
    if (!isSending) {
      hasRefetchedRef.current = false;
      return;
    }

    // If we have a timestamp, set up timeouts (isSending is guaranteed true here)
    if (pendingSendTimestamp) {
      const sentAt = new Date(pendingSendTimestamp).getTime();
      const elapsed = Date.now() - sentAt;

      // Calculate remaining time for each threshold
      const staleRemaining = STALE_MESSAGE_THRESHOLD_MS - elapsed;
      const errorRemaining = MESSAGE_TIMEOUT_MS - elapsed;

      // Set up stale message check (refetch conversation)
      if (staleRemaining > 0 && !hasRefetchedRef.current) {
        staleTimeoutRef.current = setTimeout(() => {
          // Refetch the conversation to see if message arrived via a different path
          if (currentConversation?.id) {
            console.log(
              "[useMessageTimeout] Stale message threshold reached, refetching conversation"
            );
            hasRefetchedRef.current = true;
            dispatch(selectConversation(currentConversation.id));
          }
        }, staleRemaining);
      }

      // Set up error timeout
      if (errorRemaining > 0) {
        errorTimeoutRef.current = setTimeout(() => {
          console.log("[useMessageTimeout] Message timeout reached");
          dispatch(handleMessageTimeout());
        }, errorRemaining);
      } else {
        // Already timed out
        dispatch(handleMessageTimeout());
      }
    }

    return () => {
      if (staleTimeoutRef.current) {
        clearTimeout(staleTimeoutRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [isSending, pendingSendTimestamp, currentConversation?.id, dispatch]);
}
